import { Col, Row, Spin, Table, Tag } from "antd";
import { StyleSheet } from "../../../StyleSheet";
import { useCallback, useEffect, useState } from "react";
import { UserResponse, Status, Role, UpdateMultipleUsersRequest, UserData } from "../../../types/types";
import { EditOutlined, LoadingOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import Search from "antd/es/input/Search";
import { enabledOrDisabledUserList, getUsers } from "../../../methods/axiosMethods";
import { useSelector } from "react-redux";
import _ from "lodash";
import SubDefaultLayout from "../../../components/SubDefaultLayout";
import { subMenusOfSettings } from "../../../commons/subMenus";
import Buttons from "../../../components/Buttons";
import CardDashboard from "../../../components/CardDashboard";
import { image } from "../../../commons/ImageRoutes";
import ModalNotification, { AlertTypeNew } from "../../../components/ModalNotification";
import { logout } from "../../../context/keycloak";
import CustomPagination from "../../../components/CustomPagination";

const UsersTabContent = () => {
    const navigate = useNavigate();
    const [userList, setUserList] = useState<Array<UserResponse>>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const hasSelected = selectedRowKeys.length > 0;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [openNotification, setOpenNotification] = useState(false);
    const [message, setMessage] = useState("");
    const session = useSelector((state: any) => state.user);
    const [sesionUserId, setSesionUserId] = useState("");
    const [notificationType, setNotificationType] = useState<AlertTypeNew>("SUCCESS");
    const [totalPerPage, setTotalPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchWord, setSearchWord] = useState("");

    useEffect(() => {
        if (session && session.isLogged) {
            if (session.userInfo.id !== undefined) {
                setSesionUserId(session.userInfo.id);
            }
        }
    }, [session]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        // si el registro que se está checkeando es igual al del usuario logeado, no se permite checkear
        const updatedSelectedRowKeys = newSelectedRowKeys.filter(key => key !== sesionUserId);
        setSelectedRowKeys(updatedSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const columns: ColumnsType<UserResponse> = [
        {
            title: "Acciones",
            dataIndex: "operation",
            key: "operation",
            width: "10%",
            className: 'custom-header',
            align: "center",
            render: (event, record: UserResponse) => (
                <Buttons
                    type="primary"
                    title="Editar"
                    color="#3d3dff"
                    action={() => navigate(`/settings/${record.id}`, { state: record })}
                    icon={<EditOutlined />}
                />
            ),
        },
        {
            title: "Estatus",
            dataIndex: "enabled",
            key: "enabled",
            className: 'custom-header',
            render: (enabled: boolean) => (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            margin: 5,
                            backgroundColor: (enabled) ? "#0fe248" : "#ff3d6b",
                        }}
                    />
                    <label
                        style={{
                            color: (enabled) ? "#0fe248" : "#ff3d6b",
                        }}
                    >
                        {enabled ? "Activo" : "Inactivo"}
                    </label>
                </div>
            ),
        },
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
            className: 'custom-header',
        },
        {
            title: "Apellido",
            dataIndex: "lastName",
            key: "lastName",
            className: 'custom-header',
        },
        {
            title: "Nombre de Usuario",
            dataIndex: "username",
            key: "username",
            className: 'custom-header',
        },
        {
            title: "E-mail",
            dataIndex: "email",
            key: "email",
            className: 'custom-header',
        },
        {
            title: "Tipo de Usuario",
            dataIndex: "userType",
            key: "userType",
            className: 'custom-header',
            render: (userType: Role) => (
                <Tag style={{ fontSize: 12 }} color={userType === Role.ADMIN ? "blue" : "purple"}>
                    {userType === Role.ADMIN ? "ADMINISTRADOR" : "USUARIO"}
                </Tag>
            ),
        },
    ];

    useEffect(() => {
        setCurrentPage(1);
    }, [totalPerPage]);

    const fetchData = useCallback(
        (page?: number, pageChanged?: number, searchTerm?: string) => {
            setIsLoading(true);
            const currentPageTmp = page ? page - 1 : 0;
            const currentPageChangedTmp = pageChanged ?? 10;
            const searchTermTmp = searchTerm ? searchTerm : "";
            getUsers(currentPageTmp, currentPageChangedTmp, searchTermTmp)
                .then((response) => {
                    const { data } = response;
                    if (data?.status === Status.OK && data?.data?.content) {
                        const accountsList = data.data.content;
                        setUserList(accountsList);
                        setTotalRecords(data.data.totalElements);
                    } else {
                        setUserList([]);
                        setTotalRecords(0);
                    }
                    setIsLoading(false);
                })
                .catch(() => {
                    setUserList([]);
                    setTotalRecords(0);
                    setIsLoading(false);
                    setNotificationType('ERROR');
                    setMessage("Hubo un error al obtener la lista de usuarios, intente más tarde.");
                    setOpenNotification(true);
                })
        }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchData(page, totalPerPage, searchWord);
    };

    const handleTotalPerPage = (total: number) => {
        setTotalPerPage(total);
        fetchData(1, total, searchWord);
    };

    const handleSearch = (text: string) => {
        setSearchWord(text);
        fetchData(1, totalPerPage, text);
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleModifyEnabled = (enable: boolean) => {
        const dataUser: UpdateMultipleUsersRequest = { users: [] };
        selectedRowKeys.forEach((element) => {
            userList.forEach((user: UserResponse) => {
                if (element.valueOf()) {
                    if (user.id === element) {
                        let usersData: UserData = {
                            userId: user.id,
                            enabled: enable
                        };
                        dataUser.users.push(usersData)
                    }
                }
            })
        })

        setIsLoading(true);
        enabledOrDisabledUserList(dataUser)
            .then((response) => {
                if (response.data && response.data.status) {
                    setOpenNotification(true);
                    setMessage(response.data.message);
                    if (response.data.status === Status.UPDATED) {
                        setNotificationType("SUCCESS");
                        setSelectedRowKeys([]);
                    } else {
                        setNotificationType("ERROR");
                    }
                }
                setIsLoading(false);
                fetchData(currentPage, totalPerPage);
            })
            .catch(() => {
                setNotificationType('ERROR');
                setMessage("Hubo un error al actualizar el estado");
                setOpenNotification(true);
                setIsLoading(false);
                fetchData(currentPage, totalPerPage);
            })
    };

    return (
        <SubDefaultLayout childrenSubMenu={subMenusOfSettings} title="Configuración">
            <Row gutter={[15, 25]}>
                <Col span={24}>
                    <Row>
                        <Col span={24} md={6} lg={4}>
                            <CardDashboard
                                title={"Crear Usuario"}
                                icon={image.createUserIcon}
                                action={"/settings/add-user"}
                                height={135}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col span={24}>
                    <div style={styles.primaryContainer}>
                        <Row gutter={[16, 12]} style={styles.rowContainer}>
                            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={5}>
                                <Search
                                    onSearch={(value: string) => handleSearch(value)}
                                    onChange={(value) =>
                                        _.isEmpty(value.target.value)
                                            ? (
                                                setSearchWord(""),
                                                fetchData(1, totalPerPage),
                                                setCurrentPage(1)
                                            )
                                            : null
                                    }
                                    placeholder="Buscar por nombre, username o email"
                                />
                            </Col>
                            <Col xs={0} sm={0} md={0} lg={8} xl={8} xxl={14} />
                            <Col
                                xs={24}
                                sm={24}
                                md={12}
                                lg={8}
                                xl={8}
                                xxl={5}
                                style={{ display: 'flex', justifyContent: 'space-evenly' }}
                            >
                                <Buttons
                                    type="primary"
                                    title="Habilitar"
                                    action={() => handleModifyEnabled(true)}
                                    icon={<PlusOutlined />}
                                    isDisabled={!hasSelected}
                                    color="#40CD9D"
                                    iconLeft={true}
                                />
                                <Buttons
                                    type="primary"
                                    title="Inhabilitar"
                                    color="#FF3D6B"
                                    action={() => handleModifyEnabled(false)}
                                    icon={<MinusOutlined />}
                                    isDisabled={!hasSelected}
                                    iconLeft={true}
                                />
                            </Col>
                        </Row>
                        <Col xs={24}>
                            <Table
                                rowSelection={rowSelection}
                                rowKey={(record) => record.id}
                                dataSource={userList}
                                columns={columns}
                                scroll={{ y: 400, x: 1300 }}
                                locale={{ emptyText: "No hay datos disponibles" }}
                                pagination={false}
                                loading={{
                                    spinning: isLoading,
                                    indicator: <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />,
                                }}
                            />
                        </Col>
                        <Col xs={24}>
                            <CustomPagination
                                currentPage={currentPage}
                                pageSize={totalPerPage}
                                paginationTotal={totalRecords}
                                onPageSizeChange={handleTotalPerPage}
                                onPageChange={handlePageChange}
                                showSizeChanger
                            />
                        </Col>
                    </div>
                </Col>
            </Row>
            <ModalNotification
                title={"Usuarios"}
                message={message}
                alertType={notificationType}
                show={openNotification}
                onClose={() =>
                    message === "Hubo un error al obtener la lista de usuarios, intente más tarde." ?
                        logout()
                        :
                        setOpenNotification(false)
                }
            />
        </SubDefaultLayout>
    );
};

const styles = StyleSheet.create({
    primaryContainer: {
        width: "100%",
        margin: "20px auto",
        padding: "20px",
        borderRadius: 10,
        backgroundColor: 'white',
        boxShadow: "rgba(177, 179, 185, 0.51) 5px 8px 24px 5px",
    },
    title: {
        paddingLeft: "8px",
        fontSize: 18,
    },
    rowContainer: {
        marginBottom: '20px',
    },
});

export default UsersTabContent;
